import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Form, FormGroup, Label, Input, Button, Alert, Container, Card, CardBody, CardHeader } from '../shim/reactstrap';
import { useAuth } from '../contexts/AuthContext';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  // Get the redirect path from location state, or default to home
  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await login(email, password, remember);
      // Redirect to the page they tried to visit or home
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container className="mt-5">
      <div className="row justify-content-center">
        <div className="col-md-6 col-lg-5">
          <Card>
            <CardHeader>
              <h4 className="mb-0">Login to WaveWise</h4>
            </CardHeader>
            <CardBody>
              {error && <Alert color="danger" fade={false}>{error}</Alert>}
              <Form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label for="email">Email</Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    disabled={isSubmitting}
                  />
                </FormGroup>
                <FormGroup>
                  <Label for="password">Password</Label>
                  <Input
                    type="password"
                    id="password"
                    name="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    disabled={isSubmitting}
                  />
                </FormGroup>
                <FormGroup check>
                  <Label check>
                    <Input
                      type="checkbox"
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                      disabled={isSubmitting}
                    />{' '}
                    Remember me
                  </Label>
                </FormGroup>
                <Button color="primary" type="submit" block className="mt-3" disabled={isSubmitting}>
                  {isSubmitting ? 'Logging in...' : 'Login'}
                </Button>

                <div className="text-center my-3">
                  <small className="text-muted">OR</small>
                </div>

                <Button
                  color="light"
                  block
                  className="d-flex align-items-center justify-content-center"
                  style={{ border: '1px solid #ddd' }}
                  onClick={() => window.location.href = `/api/auth/google?returnUrl=${encodeURIComponent(from)}`}
                  disabled={isSubmitting}
                >
                  <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" style={{ marginRight: '8px' }}>
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                    <path fill="none" d="M0 0h48v48H0z"/>
                  </svg>
                  Sign in with Google
                </Button>

                <Button
                  color="dark"
                  block
                  className="d-flex align-items-center justify-content-center mt-2"
                  onClick={() => window.location.href = `/api/auth/apple?returnUrl=${encodeURIComponent(from)}`}
                  disabled={isSubmitting}
                >
                  <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 814 1000" style={{ marginRight: '8px', fill: 'white' }}>
                    <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-42.4-150.3-109.3C88 429.6 77.6 346 77.6 262.8c0-122.5 55.1-198.9 140.2-214.7 55.4-10.2 109.1 9.1 150.3 9.1 33.6 0 102.9-21.5 168.5-21.5 40.8 0 148.4 3.2 220.5 116.6zm-189.7-142c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z"/>
                  </svg>
                  Sign in with Apple
                </Button>

                <div className="text-center mt-3">
                  <small>
                    Don't have an account?{' '}
                    <a href="/signup" onClick={(e) => { e.preventDefault(); navigate('/signup'); }}>
                      Sign up here
                    </a>
                  </small>
                </div>
              </Form>
            </CardBody>
          </Card>
        </div>
      </div>
    </Container>
  );
};
